import React, { useState, useMemo } from "react";
import { LuCoffee, LuBarChart3, LuRefreshCw } from "react-icons/lu";

import { EnhancedFilterState } from "../../pages/enhanced-dashboard";
import { Select } from "src/components/Form/select";
import Button from "src/components/Button/Button";
import { FaFilter, FaChartBar, FaExclamationTriangle } from "react-icons/fa";
import { languageTuples } from "src/utils/util";
import { UserLangCode } from "src/utils/util.types";
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from "recharts";
import { useTrendData } from "../../hooks/useTrendData";
import { TrendChartLoading } from "./trend-chart-loading";
import { TrendDataFilters } from "../../services/trend-data.service";

// Enhanced Lounge Statistics Section - Now uses proper lounge trend data instead of sessions fallback
type Props = {
  filters: EnhancedFilterState;
};

const chartTypeOptions = [
  { value: "line", label: "Line Chart" },
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
];

const timeGranularityOptions = [
  { value: "daily", label: "Daily (Last 30 Days)" },
  { value: "weekly", label: "Weekly (Last 12 Weeks)" },
  { value: "custom", label: "Custom Date Range" },
];

const loungeStatusOptions = [
  { value: "ALL", label: "All Statuses" },
  { value: "STANDBY", label: "Standby" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "EXPIRED", label: "Expired" },
];

const initiatorTypeOptions = [
  { value: "ALL", label: "All Initiator Types" },
  { value: "LEARNER", label: "Learners" },
  { value: "COACH", label: "Coaches" },
];

export const EnhancedLoungeStatsSection: React.FC<Props> = ({ filters }) => {
  const [sectionFilters, setSectionFilters] = useState({
    chartType: "line",
    timeGranularity: "daily",
    language: undefined as UserLangCode | undefined,
    loungeStatus: "ALL" as "ALL" | "STANDBY" | "CANCELLED" | "ACCEPTED" | "EXPIRED",
    initiatorType: "ALL" as "ALL" | "LEARNER" | "COACH",
    showFilters: false,
    startDate: undefined as Date | undefined,
    endDate: new Date() as Date | undefined, // Default to current day
  });

  // Convert section filters to API filters
  const apiFilters: TrendDataFilters = useMemo(() => ({
    timeGranularity: sectionFilters.timeGranularity as "daily" | "weekly" | "custom",
    metric: "lounge", // Now using the proper lounge metric
    role: "ALL",
    language: sectionFilters.language,
    loungeStatus: sectionFilters.loungeStatus,
    initiatorType: sectionFilters.initiatorType,
    startDate: sectionFilters.startDate,
    endDate: sectionFilters.endDate,
  }), [sectionFilters.timeGranularity, sectionFilters.language, sectionFilters.loungeStatus, sectionFilters.initiatorType, sectionFilters.startDate, sectionFilters.endDate]);

  // Fetch real trend data using the custom hook
  const { data: trendData, loading, error, refetch, isInitialLoading } = useTrendData(apiFilters);

  const handleSectionFilterChange = (key: string, value: string | boolean) => {
    setSectionFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleFilters = () => {
    setSectionFilters(prev => ({ ...prev, showFilters: !prev.showFilters }));
  };

  const renderChart = () => {
    if (!trendData?.data) return null;
    
    const commonProps = {
      data: trendData.data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const renderTooltip = (props: any) => {
      const { active, payload, label } = props;
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium text-gray-900">{label}</p>
            {payload.map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    switch (sectionFilters.chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                dot={{ fill: "#8b5cf6", strokeWidth: 2, r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="total" 
                stroke="#8b5cf6" 
                fill="#8b5cf6" 
                fillOpacity={0.3}
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Bar dataKey="total" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuCoffee className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Lounge Activity Trends</h2>
      </div>
      
      <div className="flex items-center justify-end mb-6">
        <Button
          type="button"
          onClick={toggleFilters}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-200 transform hover:scale-105"
        >
          <FaFilter className="text-sm" />
          {sectionFilters.showFilters ? "Hide" : "Show"} Filters
        </Button>
      </div>

      {/* Section-Specific Filters */}
      {sectionFilters.showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FaChartBar className="text-gray-500" />
            Chart Configuration
          </h3>
                     <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuBarChart3 className="text-gray-500" />
                Chart Type
              </label>
              <Select
                className="w-full"
                options={chartTypeOptions}
                label=""
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(val) => handleSectionFilterChange("chartType", String(val) || "line")}
              />
            </div>
            
                         <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCoffee className="text-gray-500" />
                 Time Granularity
               </label>
               <Select
                 className="w-full"
                 options={timeGranularityOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("timeGranularity", String(val) || "daily")}
               />
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCoffee className="text-gray-500" />
                 Language
               </label>
               <Select
                 className="w-full"
                 options={languageTuples}
                 label=""
                 includeOption="All Languages"
                 getOptionValue={(tuple) => tuple[0]}
                 getOptionLabel={(tuple) => tuple[1]}
                 onChange={(val) => handleSectionFilterChange("language", val as UserLangCode)}
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCoffee className="text-gray-500" />
                 Lounge Status
               </label>
               <Select
                 className="w-full"
                 options={loungeStatusOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("loungeStatus", String(val) || "ALL")}
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCoffee className="text-gray-500" />
                 Initiator Type
               </label>
               <Select
                 className="w-full"
                 options={initiatorTypeOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("initiatorType", String(val) || "ALL")}
               />
             </div>
            
                         {sectionFilters.timeGranularity === "custom" && (
               <>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                     <LuCoffee className="text-gray-500" />
                     Start Date
                   </label>
                   <input
                     type="date"
                     value={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : ""}
                     max={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : undefined}
                     onChange={(e) => {
                       const startDate = e.target.value ? new Date(e.target.value) : undefined;
                       setSectionFilters(prev => ({ 
                         ...prev, 
                         startDate,
                         // Reset end date if start date is after current end date
                         endDate: prev.endDate && startDate && startDate > prev.endDate ? undefined : prev.endDate
                       }));
                     }}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                     <LuCoffee className="text-gray-500" />
                     End Date
                   </label>
                   <input
                     type="date"
                     value={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : ""}
                     min={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : undefined}
                     max={new Date().toISOString().split('T')[0]}
                     onChange={(e) => {
                       const endDate = e.target.value ? new Date(e.target.value) : undefined;
                       setSectionFilters(prev => ({ 
                         ...prev, 
                         endDate
                       }));
                     }}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                   />
                 </div>
                 <div className="col-span-full">
                   <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
                     <p>⚠️ <strong>Note:</strong> Large date ranges may take longer to load. For best performance, keep ranges under 90 days.</p>
                   </div>
                 </div>
               </>
             )}
          </div>
          
                     <div className="mt-4 flex flex-wrap gap-2">
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
               Chart: {chartTypeOptions.find(c => c.value === sectionFilters.chartType)?.label}
             </span>
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
               Period: {timeGranularityOptions.find(t => t.value === sectionFilters.timeGranularity)?.label}
             </span>
             {sectionFilters.language && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                 Language: {languageTuples.find(t => t[0] === sectionFilters.language)?.[1] || sectionFilters.language}
               </span>
             )}
             {sectionFilters.loungeStatus !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                 Status: {loungeStatusOptions.find(s => s.value === sectionFilters.loungeStatus)?.label}
               </span>
             )}
             {sectionFilters.initiatorType !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                 Initiator: {initiatorTypeOptions.find(i => i.value === sectionFilters.initiatorType)?.label}
               </span>
             )}
           </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            Lounge Activity Trends
          </h4>
        </div>
        
        {/* Loading State */}
        {isInitialLoading && (
          <TrendChartLoading 
            chartType={sectionFilters.chartType as "line" | "area" | "bar"}
            timeGranularity={sectionFilters.timeGranularity}
            metric="lounge"
          />
        )}
        
        {/* Error State */}
        {error && !isInitialLoading && (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Failed to Load Lounge Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button
                type="button"
                onClick={refetch}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <LuRefreshCw className="text-sm" />
                Retry
              </Button>
            </div>
          </div>
        )}
        
        {/* Chart or Loading State */}
        {!isInitialLoading && !error && (
          loading ? (
            <TrendChartLoading 
              chartType={sectionFilters.chartType as "line" | "area" | "bar"}
              timeGranularity={sectionFilters.timeGranularity}
              metric="lounge"
            />
          ) : (
            renderChart()
          )
        )}
      </div>

    </div>
  );
};
